/**
 * VM-14b — Send-Modal für den Composer.
 * 2026-02 — Multi-To-Auswahl + BCC (Kontakt-Suche & Freitext).
 */
import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { EmailAddressFields } from "./EmailAddressFields";
import WetSignaturePanel, { wetSignReady } from "./WetSignaturePanel";
import {
  Envelope, EnvelopeSimple, PaperPlaneTilt, Printer, Plus, X, MagnifyingGlass, Check,
} from "@phosphor-icons/react";

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
}

export default function SendMethodModal({
  open, onOpenChange, selectedRecipients, recipientCandidates,
  channels, setChannels,
  emailOverride, setEmailOverride,
  emailToList = [], setEmailToList = () => {},
  emailBcc = [], setEmailBcc = () => {},
  emailCc = [], setEmailCc = () => {},
  onConfirm, sending,
  availableEmails = [],
  printAfterPost = false, setPrintAfterPost = () => {},
  wet = null, wetDisabled = false,
}) {
  const uniqueAddresses = (recipientCandidates || [])
    .filter((r) => selectedRecipients?.includes(r.contact_id || r.id))
    .map((r) => ({
      contact_id: r.contact_id || r.id,
      name: r.name || r.label,
      address: r.address || {},
      email: r.email,
    }));
  const differentAddresses = uniqueAddresses.length > 1
    && new Set(uniqueAddresses.map((r) => JSON.stringify(r.address))).size > 1;

  const toggleTo = (email) => {
    if (!email) return;
    const next = emailToList.includes(email)
      ? emailToList.filter((e) => e !== email)
      : [...emailToList, email];
    setEmailToList(next);
    // 2026-02 (iter124) — Bugfix: Das Freitext-Feld „Zusätzliche Adresse"
    // soll nicht dieselbe primäre E-Mail zeigen wie der ausgewählte Chip.
    // Wenn eine chip-basierte Auswahl existiert, wird das Freitext-Feld
    // geleert — der Nutzer kann dort echte ZUSÄTZLICHE Adressen eintragen.
    if (next.length > 0) {
      setEmailOverride("");
    }
  };

  // --- BCC-Panel: Kontakt-Suche + Freitext ---
  const [bccOpen, setBccOpen] = useState(emailBcc.length > 0);
  const [bccInput, setBccInput] = useState("");
  const [bccResults, setBccResults] = useState([]);
  const [bccSearching, setBccSearching] = useState(false);
  const bccDebounce = useRef(null);

  useEffect(() => { if (emailBcc.length > 0) setBccOpen(true); }, [emailBcc.length]);

  const searchBccContacts = (q) => {
    setBccInput(q);
    if (bccDebounce.current) clearTimeout(bccDebounce.current);
    if (!q || q.length < 2) { setBccResults([]); return; }
    setBccSearching(true);
    bccDebounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/contacts?q=${encodeURIComponent(q)}&limit=8`);
        const list = Array.isArray(data) ? data : (data?.items || []);
        // Für jeden Kontakt die primäre E-Mail einsammeln
        const flat = [];
        for (const c of list) {
          const em = (c.emails || []).find((e) => (typeof e === "object" ? e.is_primary : false))
            || c.emails?.[0];
          const email = typeof em === "string" ? em : em?.email;
          if (email) flat.push({ email, label: c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() });
        }
        setBccResults(flat);
      } catch { setBccResults([]); }
      finally { setBccSearching(false); }
    }, 200);
  };

  const addBcc = (email) => {
    const clean = String(email || "").trim();
    if (!isValidEmail(clean)) return;
    if (emailBcc.includes(clean)) return;
    setEmailBcc([...emailBcc, clean]);
    setBccInput("");
    setBccResults([]);
  };
  const removeBcc = (email) => setEmailBcc(emailBcc.filter((e) => e !== email));

  const onBccKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && bccInput.trim()) {
      e.preventDefault();
      addBcc(bccInput);
    } else if (e.key === "Backspace" && !bccInput && emailBcc.length > 0) {
      removeBcc(emailBcc[emailBcc.length - 1]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="send-method-modal">
        <DialogHeader>
          <DialogTitle>Wie soll das Schreiben versendet werden?</DialogTitle>
          <DialogDescription>
            Versandweg antippen – auch beide möglich.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Versandweg">
          {[["email", Envelope, "E-Mail", "über Microsoft 365 / Resend"], ["post", EnvelopeSimple, "Post", "PDF-Brief + Versandbeleg"]].map(([k, Icon, title, sub]) => (
            <button key={k} type="button" role="checkbox" aria-checked={channels[k]} onClick={() => setChannels((s) => ({ ...s, [k]: !s[k] }))} data-testid={`send-ch-${k}`}
              className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${channels[k] ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}>
              <span className={`h-5 w-5 shrink-0 rounded border-2 grid place-items-center ${channels[k] ? "bg-slate-900 border-slate-900 text-white" : "border-slate-300"}`}>{channels[k] && <Check size={12} weight="bold" />}</span>
              <span className="min-w-0"><span className="flex items-center gap-1.5 text-sm font-semibold"><Icon size={15} /> {title}</span><span className="block text-[11px] text-slate-500">{sub}</span></span>
            </button>
          ))}
        </div>
        {channels.email && (
          <div className="rounded-md border border-slate-200 p-3" data-testid="send-email-details">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">E-Mail-Empfänger</div>
            <EmailAddressFields available={availableEmails} to={emailToList} setTo={(v) => { setEmailToList(v); setEmailOverride(""); }}
              cc={emailCc} setCc={setEmailCc} bcc={emailBcc} setBcc={setEmailBcc} disabled={false} />
          </div>
        )}
        {channels.post && (
          <label className="flex items-center gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 cursor-pointer" data-testid="send-post-details">
            <Checkbox checked={printAfterPost} onCheckedChange={setPrintAfterPost} data-testid="send-print-after-post" />
            <Printer size={14} className="text-slate-600" />
            <span>Nach dem Speichern direkt drucken</span>
          </label>
        )}

        {wet && (channels.email || channels.post) && (
          <WetSignaturePanel wet={wet} channels={channels} disabled={wetDisabled} />
        )}

        {differentAddresses && (
          <div className="border border-amber-200 bg-amber-50 rounded p-3 text-xs" data-testid="different-addresses-hint">
            <div className="font-semibold text-amber-900 mb-1">⚠ Empfänger haben unterschiedliche Adressen</div>
            <div className="text-amber-800">Ein Duplikat wird an jede Adresse geschickt:</div>
            <ul className="mt-1 space-y-0.5">
              {uniqueAddresses.map((r) => (
                <li key={r.contact_id} className="text-slate-700">
                  <span className="font-medium">{r.name}</span> — {r.address?.street || "?"} {r.address?.house_number || ""}, {r.address?.zip || ""} {r.address?.city || ""}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Abbrechen</Button>
          <Button
            className="bg-slate-900 hover:bg-slate-800"
            onClick={onConfirm}
            disabled={sending || (!channels.email && !channels.post) || (wet && !wetDisabled && !wetSignReady(wet, channels))}
            data-testid="send-confirm-btn"
          >
            <PaperPlaneTilt size={14} className="mr-1.5" />
            {sending ? "Versende…" : "Jetzt versenden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
